import { toApiError } from '@/lib/http';

/** The admin console's reads. */

export interface Overview {
  readonly khutbahs: number;
  readonly published: number;
  readonly processing: number;
  readonly failed: number;
  readonly liveUnreviewed: number;
  readonly deadJobs: number;
  readonly runningJobs: number;
  /** False today: nothing records a faithfulness check. */
  readonly faithfulnessCheckRuns: boolean;
}

export interface KhutbahRow {
  readonly id: string;
  readonly friday: string;
  readonly institutionKey: string;
  readonly institutionNameAr: string | null;
  readonly titleOriginal: string | null;
  readonly status: string;
  readonly visibility: string;
  readonly sourceKind: string;
  readonly passages: number;
  readonly languages: number;
  readonly reviewedLanguages: number;
  /** Fields extraction guessed that nobody has confirmed. */
  readonly needsConfirmation: readonly string[];
}

export interface JobRow {
  readonly id: string;
  readonly type: string;
  readonly status: string;
  readonly attempts: number;
  readonly maxAttempts: number;
  readonly progress: number;
  readonly lastError: string | null;
  readonly language: string | null;
  readonly createdAt: string | null;
}

function str(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

function num(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

async function get(url: string, signal: AbortSignal): Promise<unknown> {
  const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw await toApiError(response);
  return response.json();
}

export async function fetchOverview(signal: AbortSignal): Promise<Overview> {
  const body = await get('/api/editor/admin/overview', signal);
  const row = (typeof body === 'object' && body !== null ? body : {}) as Record<
    string,
    unknown
  >;
  return {
    khutbahs: num(row, 'khutbahs'),
    published: num(row, 'published'),
    processing: num(row, 'processing'),
    failed: num(row, 'failed'),
    liveUnreviewed: num(row, 'liveUnreviewed'),
    deadJobs: num(row, 'deadJobs'),
    runningJobs: num(row, 'runningJobs'),
    faithfulnessCheckRuns: row.faithfulnessCheckRuns === true,
  };
}

export async function fetchAdminKhutbahs(
  status: string | null,
  signal: AbortSignal,
): Promise<KhutbahRow[]> {
  const url = status
    ? `/api/editor/admin/khutbahs?status=${encodeURIComponent(status)}`
    : '/api/editor/admin/khutbahs';
  const body = await get(url, signal);
  if (!Array.isArray(body)) return [];
  return body.flatMap((entry): KhutbahRow[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const row = entry as Record<string, unknown>;
    const id = str(row, 'id');
    if (id === null) return [];
    return [
      {
        id,
        friday: str(row, 'friday') ?? '',
        institutionKey: str(row, 'institutionKey') ?? '',
        institutionNameAr: str(row, 'institutionNameAr'),
        titleOriginal: str(row, 'titleOriginal'),
        status: str(row, 'status') ?? 'draft',
        visibility: str(row, 'visibility') ?? 'private',
        sourceKind: str(row, 'sourceKind') ?? 'publisher',
        passages: num(row, 'passages'),
        languages: num(row, 'languages'),
        reviewedLanguages: num(row, 'reviewedLanguages'),
        needsConfirmation: Array.isArray(row.needsConfirmation)
          ? row.needsConfirmation.filter((f): f is string => typeof f === 'string')
          : [],
      },
    ];
  });
}

export async function fetchJobs(
  status: string | null,
  signal: AbortSignal,
): Promise<JobRow[]> {
  const url = status
    ? `/api/editor/admin/jobs?status=${encodeURIComponent(status)}`
    : '/api/editor/admin/jobs';
  const body = await get(url, signal);
  if (!Array.isArray(body)) return [];
  return body.flatMap((entry): JobRow[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const row = entry as Record<string, unknown>;
    const id = str(row, 'id');
    if (id === null) return [];
    return [
      {
        id,
        type: str(row, 'type') ?? '',
        status: str(row, 'status') ?? '',
        attempts: num(row, 'attempts'),
        maxAttempts: num(row, 'maxAttempts'),
        progress: num(row, 'progress'),
        lastError: str(row, 'lastError'),
        language: str(row, 'language'),
        createdAt: str(row, 'createdAt'),
      },
    ];
  });
}
